import * as React from "react";
import { observer, inject } from "mobx-react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Box,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import BackIcon from "@mui/icons-material/ArrowBack";
import LogoutButton from "../common/LogoutButton";
import autobind from "autobind-decorator";
import AccountService from "../../service/AccountService";
import { RouterStore } from "mobx-react-router";
import { observable } from "mobx";
import FileService, { NarrativeMetadata } from "../../service/FileService";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { withStyles } from "@mui/styles";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { async } from "@firebase/util";

const Root = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100%",
});

const LoaderContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  alignItems: "center",
  justifyContent: "center",
});

type NarrativePageProps = {
  routing?: RouterStore;
  account?: AccountService;
  files?: FileService;
  match?: any;
};

const QuestionText = styled("p")({
  margin: "12px",
  fontSize: "32px",
  minHeight: "32px",
  color: "rgb(80, 80, 80)",
});

const NarrativeText = styled("p")({
  margin: "12px",
  fontSize: "14",
  color: "rgb(120, 120, 120)",
});

const NavButton = withStyles({
  label: {
    color: "#FFF",
  },
})(Button);

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

type EditorPageStatus = "LOADING" | "NORMAL";

@inject("account")
@inject("routing")
@inject("files")
@observer
export default class NarrativePage extends React.Component<NarrativePageProps> {
  @observable
  private _status: EditorPageStatus = "LOADING";

  @observable
  private _userName = "";

  @observable
  private _narrative = "";

  @observable
  private _responses = [];

  @observable
  private _narrativeData: any = {};

  @observable
  private tabValue = 0;

  @autobind
  goBack() {
    var { routing } = this.props;
    routing.replace("/narratives");
  }

  @autobind
  handleTabChange(event, newVal) {
    this.tabValue = newVal;
  }

  @autobind
  gotoPlan() {
    var { routing } = this.props;
    routing.push(`/plan`);
  }

  async getDownloadFileLink(files: any) {
    const { id } = this.props.match.params;
    const { account } = this.props;
    const userId = account.user.uid;
    const storage = getStorage();
    const path = `uploaded-files/${userId}/${this._narrativeData.flowId}/${id}`;
    if (!files || files.length === 0) return "";
    const links = await Promise.all([
      ...files?.map(async (fileName: any) => {
        return {
          fileName,
          url: await getDownloadURL(ref(storage, `${path}/${fileName}`)),
        };
      }),
    ]);
    return links;
  }

  async componentDidMount() {
    const { id } = this.props.match.params;
    const { routing, files, account } = this.props;

    const userId = account.user.uid;

    if (id == null) {
      routing.replace("/");
      return;
    }

    this._status = "LOADING";

    var narrativeData = await files.loadNarrativeData(id);

    if (narrativeData == "UNKNOWN_ERROR") {
      routing.replace("/");
      return;
    }

    var nd = narrativeData as NarrativeMetadata;

    this._narrativeData = nd;

    this._userName = nd.userName;
    this._narrative = nd.narrative;
    this._responses = await Promise.all([
      ...nd.responses.map(async (resp) => {
        return {
          a: resp.a,
          q: resp.q,
          uploadedFilesLinks: await this.getDownloadFileLink(
            resp.uploadedFilesLinks
          ),
        };
      }),
    ]);

    this._status = "NORMAL";
  }

  renderNavBar() {
    var userType = this.props.account.userData.userType;
    return (
      <AppBar position="static">
        <Toolbar variant="dense">
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={this.goBack}
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h6" style={{ flexGrow: 1, color: "inherit" }}>
            {this._status == "LOADING" ? "Loading..." : this._userName}
          </Typography>

          {userType == "FREE_USER" && (
            <NavButton aria-label="menu" onClick={this.gotoPlan}>
              <UnarchiveIcon style={{ marginRight: "10px" }} />
              UPGRADE
            </NavButton>
          )}
          <LogoutButton />
        </Toolbar>
      </AppBar>
    );
  }

  renderLoading() {
    return (
      <LoaderContainer>
        <CircularProgress />
      </LoaderContainer>
    );
  }

  renderNarrative() {
    return (
      <div
        style={{
          marginLeft: "12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <QuestionText> Narrative for {this._userName}</QuestionText>

        {this._narrative.split(".").map((entry, index) => (
          <NarrativeText key={index}>{`${entry}.`}</NarrativeText>
        ))}
      </div>
    );
  }

  renderResponses() {
    return (
      <Box
        marginLeft="12px"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="flex-start"
          minWidth="80%"
          margin="20px"
        >
          <Typography minWidth="60%" fontWeight="bold">
            Questions
          </Typography>
          <Typography minWidth="10%" fontWeight="bold">
            :
          </Typography>
          <Typography minWidth="30%" fontWeight="bold">
            Answers
          </Typography>
        </Box>
        {this._responses?.map((entry, index) => (
          <Box
            key={index}
            display="flex"
            flexDirection="row"
            alignItems="flex-start"
            minWidth="80%"
            margin="10px"
          >
            <Typography minWidth="60%">{entry.q}</Typography>
            <Typography minWidth="10%">:</Typography>
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Typography minWidth="30%">{entry.a}</Typography>
              {entry?.uploadedFilesLinks &&
                entry?.uploadedFilesLinks.map(({ fileName, url }) => (
                  <a download={fileName} href={url} target="_blank">
                    {fileName}
                  </a>
                ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  renderNarrativeTabs() {
    return (
      <Paper square>
        <Tabs
          value={this.tabValue}
          indicatorColor="primary"
          textColor="primary"
          onChange={this.handleTabChange}
        >
          <Tab label="Responses"></Tab>
          <Tab label="Narrative"></Tab>
        </Tabs>
        <TabPanel value={this.tabValue} index={0}>
          {this.renderResponses()}
        </TabPanel>
        <TabPanel value={this.tabValue} index={1}>
          {this.renderNarrative()}
        </TabPanel>
      </Paper>
    );
  }

  render() {
    return (
      <Root>
        <div
          style={{
            width: "100%",
            height: "env(safe-area-inset-top)",
            backgroundColor: "#3f51b5",
          }}
        />
        {this.renderNavBar()}
        {this._status == "LOADING"
          ? this.renderLoading()
          : this.renderNarrativeTabs()}
      </Root>
    );
  }
}
